import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useState } from "react";
import { Printer, CheckCircle2, AlertCircle, Circle, FileText, Download, Loader2, Lock } from "lucide-react";
import type { Client } from "@shared/schema";
import { HELD_PRIVATELY_COPY } from "@shared/constants/gov-form-framing";
import { countFormFields, type ReadinessField } from "@shared/readiness-summary";

/** GOVID-READINESS-PRIVATE-STATE-1 (s201): the row shape — including the third `heldPrivately`
 *  state — is the SHARED one, aliased rather than re-typed so the page cannot drift from the
 *  route that fills it. Counting rules live in the same module. */
type FormField = ReadinessField;

interface OntarioForm {
  title: string;
  agency: string;
  deadline: string;
  notes: string;
  reviewNote?: string;   // F-3b: lawyer/paralegal review copy on court/tax (fill_reviewed) forms
  fields: FormField[];
}

interface ExportData {
  client: { name: string; email: string };
  exportDate: string;
  summary: {
    totalFields: number;
    filledFields: number;
    missingCount: number;
    missingFields: string[];
  };
  forms: OntarioForm[];
}

interface OntarioFormsExportPageProps {
  clientData: Client | null;
}

function FieldRow({ field }: { field: FormField }) {
  const hasValue = !!field.value;
  // Third state, and there is no fourth: held privately reads as neither entered nor missing.
  // Muted, not red — nothing is wrong with this row and the client has nothing to go and fix.
  if (field.heldPrivately) {
    return (
      <div className="flex items-start gap-3 py-2 border-b last:border-0">
        <div className="mt-0.5 shrink-0">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
          <p className="text-sm mt-0.5 italic text-muted-foreground">{HELD_PRIVATELY_COPY}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0">
      <div className="mt-0.5 shrink-0">
        {hasValue ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <Circle className="h-4 w-4 text-red-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
        <p className={`text-sm mt-0.5 ${hasValue ? "text-foreground" : "text-red-500 italic"}`}>
          {hasValue ? field.value : "Not entered"}
        </p>
      </div>
    </div>
  );
}

export default function OntarioFormsExportPage({ clientData }: OntarioFormsExportPageProps) {
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    if (!clientData?.id) return;
    setPdfLoading(true);
    try {
      const res = await fetch(`/api/clients/${clientData.id}/export/ontario-forms/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ontario-forms-${clientData.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  const { data, isLoading, error } = useQuery<ExportData>({
    queryKey: ["/api/clients", clientData?.id, "export", "ontario-forms"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData!.id}/export/ontario-forms`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load export data");
      return res.json();
    },
    enabled: !!clientData?.id,
    staleTime: 0,
  });

  if (!clientData) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No client selected.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading export data…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to load export data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const exportDateStr = new Date(data.exportDate).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const fillPct = data.summary.totalFields > 0
    ? Math.round((data.summary.filledFields / data.summary.totalFields) * 100)
    : 0;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6 print:p-0 print:space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-semibold">Ontario Estate Forms — Data Package</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Prepared for <strong>{data.client.name}</strong> · {exportDateStr}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => window.print()} className="gap-2">
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button onClick={downloadPdf} disabled={pdfLoading} className="gap-2">
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Print header (hidden on screen) */}
      <div className="hidden print:block border-b pb-4">
        <h1 className="text-xl font-bold">Ontario Estate Forms — Data Package</h1>
        <p className="text-sm text-gray-600 mt-1">
          Prepared for: <strong>{data.client.name}</strong> · Generated: {exportDateStr}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          This document contains personal information. Handle with care and store securely.
        </p>
      </div>

      {/* Summary card */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="font-medium">Completion Summary</p>
            <p className="text-sm text-muted-foreground">
              {data.summary.filledFields} of {data.summary.totalFields} required fields entered ({fillPct}%)
            </p>
          </div>
          <Badge
            variant={data.summary.missingCount === 0 ? "default" : "secondary"}
            className="ml-auto shrink-0"
          >
            {data.summary.missingCount === 0 ? "All fields complete" : `${data.summary.missingCount} missing`}
          </Badge>
        </div>

        {data.summary.missingCount > 0 && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
              Missing fields — add these in your estate plan to complete the package:
            </p>
            <ul className="space-y-0.5">
              {data.summary.missingFields.map((m, i) => (
                <li key={i} className="text-xs text-amber-700 dark:text-amber-400">· {m}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <Separator />

      {/* One card per form */}
      {data.forms.map((form, i) => {
        // GOVID-READINESS-PRIVATE-STATE-1: the per-form badge counts what the summary counts,
        // through the SAME shared rule. A held-privately row is outside both, so a form whose
        // every field is held privately reads 0/0 rather than accusing the client of blanks they
        // were never asked to fill.
        const { filled: formFilled, total: formTotal } = countFormFields(form);
        const formComplete = formFilled === formTotal;

        return (
          <div key={i} className="rounded-lg border bg-card overflow-hidden print:break-inside-avoid">
            {/* Form header */}
            <div className="px-4 py-3 bg-muted/50 border-b flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-semibold text-base">{i + 1}. {form.title}</h2>
                  <Badge variant={formComplete ? "default" : "outline"} className="text-xs">
                    {formFilled}/{formTotal} fields
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  <strong>Agency:</strong> {form.agency}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  <strong>Deadline:</strong> {form.deadline}
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="px-4 py-2 bg-blue-50/50 dark:bg-blue-950/20 border-b text-xs text-blue-800 dark:text-blue-300">
              {form.notes}
            </div>

            {/* F-3b: court/tax forms carry the lawyer-review line (worksheet stays; no recreation) */}
            {form.reviewNote && (
              <div className="px-4 py-2 border-b text-xs italic" style={{ color: "#C8862E" }}>
                {form.reviewNote}
              </div>
            )}

            {/* Fields */}
            <div className="px-4 py-1">
              {form.fields.map((field, j) => (
                <FieldRow key={j} field={field} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div className="rounded-lg border p-4 text-xs text-muted-foreground space-y-1 print:mt-8">
        <p className="font-medium">Important Notes</p>
        <p>· This document is a data reference only — it does not constitute legal or financial advice.</p>
        <p>· Government forms change. Always verify current form versions at the relevant agency website.</p>
        <p>· Ontario government forms: <span className="font-mono">ontario.ca/forms</span> · Service Canada: <span className="font-mono">canada.ca/service-canada</span> · CRA: <span className="font-mono">canada.ca/cra</span></p>
        <p>· Consult the estate lawyer before filing any legal documents.</p>
      </div>
    </div>
  );
}
